import React, { Fragment, useEffect, useRef, useState } from "react";
import { ChartContainer, DonutCharts } from "../components/dashboards";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import nemo from "../images/nemo.png";
import cerebro from "../images/cerebro.png";
import a1v1 from "../images/a1va.jpg";
import { SvgIcon } from "@progress/kendo-react-common";
import { documentManagerIcon } from "@progress/kendo-svg-icons";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import {
  formatDate,
  formatDateTime,
  notificationDurationMiddle,
} from "../lib/utils";
import {
  canCreateReports,
  canEditReports,
  canViewReport,
} from "../routing/authorizationhandlers";
import { List } from "../components/list";
import { Button } from "@progress/kendo-react-buttons";
import { useEffectOnce } from "../hooks/useeffectonce";
import { addNotification } from "../redux/slices/notificationsslice";
import {
  disposePage,
  loadPage,
  setProcessing,
} from "../redux/slices/homepageslice";
import { setCurrentPage } from "../redux/slices/currentpageslice";
import { apiErrorsHandler, getErrorMessages } from "../api/httpclient";

export const HomePageUser = (props) => {
  const { currentTheme, currentUser } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pageState = useSelector((state) => state.home);
  const appearance = useSelector((state) => state.appearance);
  const [agentMounted, setAgentMounted] = useState(false);
  const { t } = useTranslation();

  let currentPageId = "home";

  const typeToCategoryMap = {
    1: t("homepageuser.alert"),
    2: t("homepageuser.message"),
    3: t("homepageuser.data"),
    4: t("homepageuser.data_correction"),
  };

  const transformedNotifications = pageState.notifications.map((item) => ({
    category: typeToCategoryMap[item.Type],
    value: item.Total,
    color: getColor(item.Type),
  }));

  function getColor(type) {
    const colorMap = {
      1: "red",
      2: "#ccc",
      3: "green",
      4: currentTheme.touchColor,
    };
    return colorMap[type] || "gray"; // Default to gray if no match
  }

  const handleOpenAgent = () => {
    setAgentMounted((prev) => !prev);
  };

  useEffect(() => {
    if (location.hash === "#ai") setAgentMounted(true);
  }, [location.hash]);

  const userName = currentUser?.FullName || currentUser?.Email || t("homepageuser.there");
  const alertNotifications = pageState.notifications.filter(
    (item) => item.Type === 1,
  );
  const alertCount = alertNotifications.reduce(
    (total, item) => total + (Number(item.Total) || 0),
    0,
  );
  const shortcuts = [
    {
      label: t("homepageuser.my_reports"),
      path: "/reports",
      icon: "chart-column-clustered",
    },
    { label: t("homepageuser.alerts"), path: "/alerts", icon: "warning" },
    {
      label: t("homepageuser.dashboards"),
      path: "/reports",
      icon: "grid",
    },
    { label: t("homepageuser.data"), path: "/datasets", icon: "data" },
    {
      label: t("homepageuser.notifications"),
      path: "/notifications",
      icon: "bell",
    },
    { label: t("homepageuser.ci_agent"), path: "/#ai", icon: "reddit" },
  ];

  /*initial page load*/
  useEffectOnce(() => {
    //if (!currentUser.IsFederationRootUser) {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("homepageuser.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("homepageuser.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
    //}
    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  const reportItemRenderer = (currentTheme) => (props, item, index) => {
    const handleViewDetails = (e, item) => {
      props.navigate("/reportingdesign/" + item.ReferenceKey);
    };

    return (
      <div
        key={item.Id}
        className={`list-item`}
        onDoubleClick={(e) => handleViewDetails(e, item)}
      >
        <div className={`list-item-data`}>
          <div className="list-detail-title">{item.Name}</div>
          <div className="list-detail-system">
            {item.IsSystem ? t("homepageuser.yes") : t("homepageuser.no")}
          </div>
          <div className="list-detail-date">
            {/* {formatDateTime(new Date(item.LastModifiedOnUTC) + "UTC", false)} */}
            {formatDateTime(
              new Date(
                item.LastModifiedOnUTC.endsWith("Z")
                  ? item.LastModifiedOnUTC
                  : `${item.LastModifiedOnUTC}Z`,
              ),
              false,
            )}
          </div>
        </div>
        <div className="list-item-view">
          {canViewReport(currentUser) && (
            <Button
              disabled={false}
              title={t("homepageuser.view")}
              style={{
                border: "none",
                color: currentTheme.touchColor,
                backgroundColor: "transparent",
              }}
              icon="eye"
              onClick={(e) =>
                props.navigate(`/reportingview/${item.ReferenceKey}`)
              }
            ></Button>
          )}
        </div>
        <div className="list-item-edit">
          {canEditReports(currentUser) && ( //!isClient &&
            <Button
              disabled={false}
              title={t("homepageuser.design")}
              style={{
                border: "none",
                color: currentTheme.touchColor,
                backgroundColor: "transparent",
              }}
              icon="edit"
              onClick={(e) =>
                props.navigate(`/reportingdesign/${item.ReferenceKey}`)
              }
            ></Button>
          )}
        </div>
      </div>
    );
  };
  return (
    <Fragment>
      <div className="home-page-user">
        <section className="mobile-home-dashboard">
          <div className="mobile-home-greeting">
            <div>
              <h1>{t("homepageuser.good_morning", { name: userName })}</h1>
              <p>{t("homepageuser.personalized_workspace")}</p>
            </div>
            <NavLink
              to="/accountmanagement"
              className="mobile-home-avatar"
              aria-label={t("accountmang.account")}
            >
              <span className="k-icon k-i-user" />
            </NavLink>
          </div>

          <NavLink to="/alerts" className="mobile-attention-card">
            <span className="mobile-attention-card-label">
              {t("homepageuser.alerts_requiring_attention")}
            </span>
            <span className="mobile-attention-card-total">
              {t(
                alertCount === 1
                  ? "homepageuser.alert_count_one"
                  : "homepageuser.alert_count_other",
                { count: alertCount },
              )}
            </span>
            <span className="mobile-attention-card-detail">
              {t("homepageuser.review_latest_alert_activity")}
            </span>
          </NavLink>

          <section className="mobile-dashboard-section">
            <div className="mobile-dashboard-section-heading">
              <h2>{t("homepageuser.your_shortcuts")}</h2>
            </div>
            <div className="mobile-shortcuts" id="shortcuts">
              {shortcuts.map((shortcut) => (
                <NavLink
                  key={shortcut.label}
                  to={shortcut.path}
                  className="mobile-shortcut"
                >
                  <span
                    className={`mobile-shortcut-icon k-icon k-i-${shortcut.icon}`}
                  />
                  <span>{shortcut.label}</span>
                </NavLink>
              ))}
            </div>
          </section>

          <section className="mobile-dashboard-section">
            <div className="mobile-dashboard-section-heading">
              <h2>{t("homepageuser.pinned_reports")}</h2>
              <NavLink to="/reports">{t("homepageuser.view_all")}</NavLink>
            </div>
            <div className="mobile-pinned-reports">
              {pageState.reports?.slice(0, 3).map((report) => (
                <NavLink
                  key={report.Id}
                  to={`/reportingview/${report.ReferenceKey}`}
                  className="mobile-pinned-report"
                >
                  <span className="mobile-pinned-report-title">
                    {report.Name}
                  </span>
                  <span className="mobile-pinned-report-meta">
                    {report.IsSystem
                      ? t("homepageuser.system_report")
                      : t("homepageuser.last_viewed_report")}
                  </span>
                </NavLink>
              ))}
              {pageState.reports?.length === 0 && (
                <div className="mobile-pinned-empty">
                  {t("homepageuser.no_pinned_reports")}
                </div>
              )}
            </div>
          </section>
        </section>
        <section className="desktop-home-dashboard">
          <div className="home-page-user-header">
            {canCreateReports(currentUser) && (
              <NavLink to="/reportingdesign/new" className="link">
                {t("homepageuser.report")}
              </NavLink>
            )}
          </div>
          <div
            className="content"
            style={{ display: "flex", gap: "24px", alignItems: "stretch" }}
          >
            {transformedNotifications.length > 0 && (
              <div
                style={{
                  padding: "20px",
                  border: "1px solid #ddd",
                  display: "flex",
                  justifyContent: "space-around",
                  direction: "ltr",
                }}
              >
                <DonutCharts
                  title={t("homepageuser.notifications")}
                  type="pie"
                  data={transformedNotifications}
                  currentTheme={currentTheme}
                  legendPosition="bottom"
                  labelContent={(e) => e.value}
                  widthChart="350px"
                />
              </div>
            )}

            <div
              style={{
                flex: "1 1 0",
                textAlign: "center",
                border: "1px solid #ddd",
                padding: "35px",
                alignContent: "center",
                direction: "ltr",
              }}
            >
              <img src={cerebro} alt="Intech" className="nemo" />
              <div className="txt">
                {t("homepageuser.cerebro")}
                <br /> {t("homepageuser.checkout")}
              </div>
              <NavLink to="https://cerebro360.ai/ic360doc" target="_blank">
                <SvgIcon
                  icon={documentManagerIcon}
                  style={{ margin: "-5px 10px 0 0" }}
                />
                {t("homepageuser.checknow")}
              </NavLink>
            </div>
            <div
              style={{
                flex: "1 1 0px",
                border: "1px solid rgb(221, 221, 221)",
                padding: "35px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
              onClick={handleOpenAgent}
            >
              <img src={a1v1} alt="A1VA AI" style={{ width: "150px" }} />
              <div className="txt">{t("homepageuser.ci_agent")}</div>
            </div>

            {/* <div
            style={{
              flex: "1 1 0",
              textAlign: "center",
              border: "1px solid #ddd",
              padding: "35px",
              alignContent: "center",
              direction: "ltr",
            }}
          >
            <img src={nemo} alt="Nemo AI" className="nemo" />
            <div className="txt">
              {t("homepageuser.nemoai")}
              <br /> {t("homepageuser.integration")}
            </div>
            <NavLink to="https://intech-mena.com/nemo-ai" target="_blank">
              {t("homepageuser.knowmore")}
            </NavLink>
          </div> */}
          </div>
          {pageState.reports?.length > 0 && (
            <div className="content">
              <div
                style={{
                  fontSize: "20px",
                  margin: "25px 5px 15px",
                  fontWeight: "bold",
                }}
              >
                {t("homepageuser.last")}
              </div>

              <div className="header-list">
                <div className="header-left">{t("homepageuser.name")}</div>
                <div className="header-center">{t("homepageuser.system")}</div>
                <div className="header-right">{t("homepageuser.modified")}</div>
              </div>
              {pageState.reports?.length > 0 && (
                <List
                  className="home-page-user-list"
                  navigate={navigate}
                  items={pageState.reports}
                  itemRenderer={reportItemRenderer(currentTheme)}
                />
              )}
            </div>
          )}

          {pageState.reports?.length <= 0 && (
            <div className="content">
              <div
                style={{
                  fontSize: "15px",
                  margin: "25px 5px 15px",
                  fontWeight: "bold",
                }}
              >
                {t("homepageuser.noreports")}
              </div>
            </div>
          )}
        </section>
      </div>
      {agentMounted && (
        <div className="a1vaWrapper">
          <button className="a1vaClose" onClick={() => setAgentMounted(false)}>
            ✕
          </button>

          <iframe
            src="https://app.a1va.ai/embed/avatar/bce9a2de-1734-4619-8662-f2374e71bc68?compact=true"
            frameBorder="0"
            allow="camera; microphone; display-capture"
            allowFullScreen
            className="a1vaIframe"
            title={t("homepageuser.ai_assistant")}
          />
        </div>
      )}
      <style>
        {`
          .home-page-user {
            display: flex;
            flex-direction: column; 
            height: 100%;
            margin:20px;
            background-image: url("backgrounds/bg2.jpg");
            background-repeat: no-repeat;
            background-size: 700px 450px;
            background-position: center;
            direction:${appearance.isRightToLeft ? "rtl" : "ltr"}
          }

          .home-page-user .home-page-user-header{
            margin-bottom:20px;
          }

          .home-page-user .home-page-user-header a{
            border: 1px solid #ccc;
            padding: 2px 10px 3px;
            color:${currentTheme.touchColor}
          }

          .home-page-user .content .txt{
            margin-top:30px;
          }

          .home-page-user .content .nemo{
            height:50px;
          }

          .home-page-user .content a{
            margin-top:30px;
            color:${currentTheme.touchColor}
          }

          .home-page-user .content  .header-list{
            display:flex;
            justify-content:space-between;
            width:calc(100% - 140px);
            padding:12px;
            font-weight:bold;
          }

          .home-page-user .content  .header-list div{
            display:flex;
            width:33.33%
            text-align:center;
          }

          .home-page-user .content .home-page-user-list
          {
              border: 1px dotted ${currentTheme.separatorColor};
              border-radius: 8px;
              margin-top: 24px;
              min-width: 700px;
              width: calc(100% - 40px);
              height: calc(100% - 46px);
              padding: 0px 0px 0px 0px;
          }

          .home-page-user .content .home-page-user-list .list-root
          {
              border-collapse: collapse;
          }

          .home-page-user .content .home-page-user-list .list-item
          {
              line-height: 3;
              border-top: 1px solid ${currentTheme.componentBorderColor};
              border-radius: 6px;
          }

          .home-page-user .content .home-page-user-list .list-item .list-item-data{
            display:flex;
            justify-content:space-between;
            width:100%
          }

          .home-page-user .content .home-page-user-list .list-item .list-item-data div{
            width:33.33%
          }

          .home-page-user .content .home-page-user-list .list-item .list-item-data div:nth-child(2){
            text-align:center;
          }

          .home-page-user .content .home-page-user-list .list-item .list-item-data div:last-child{
            text-align:right;
          }
          .home-page-user .content .home-page-user-list .list-item:first-child
          {
              border-top: none;
          }

          .home-page-user .content .home-page-user-list .list-item:last-child
          {
              border-bottom: 1px solid ${currentTheme.componentBorderColor};
          }

          .home-page-user .content .home-page-user-list .list-item:hover
          {
            background-color: ${currentTheme.componentBorderColor};
            cursor: pointer;
          }

          .home-page-user .content .home-page-user-list .list-item-view,.home-page-user .content .home-page-user-list .list-item-edit
          {
              width:50px;
              vertical-align: middle;
          } 

          .a1vaWrapper {
            position: fixed;
            bottom: 32px;
            right: 32px;
            z-index: 10000;
          }

          .a1vaIframe {
            width: 260px;
            height: 150px;
            border-radius: 16px;
            border: none;
            overflow: hidden;
            background: transparent;
          }

          .a1vaClose {
            position: absolute;
            top: -10px;
            right: -10px;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            border: none;
            cursor: pointer;
            z-index: 10001;
          }
                  
        `}
      </style>
    </Fragment>
  );
};
